// AI-assisted: Claude 2026-08-08, human-reviewed: ausstehend
// „12.480 kg" sagt niemandem etwas. „So viel wiegt ein Reisebus" schon.
// Thors Wunsch Runde 16: bewegtes Volumen in etwas übersetzen, das man sich
// vorstellen kann — von der Hauskatze bis zur Cheops-Pyramide.
//
// Die Zahlen sind gerundete Richtwerte aus gängigen Quellen (Blauwal ~190 t,
// Afrikanischer Elefant ~6 t, Breitmaulnashorn bis 3,5 t, Eiffelturm ~10.100 t,
// A320 max. Startgewicht 77 t). Sie sollen anschaulich sein, nicht amtlich —
// deshalb runde Werte statt Nachkommastellen.

export interface Vergleich {
  kg: number
  /** mit Artikel, für „So viel wiegt …" */
  eins: string
  /** Mehrzahl ohne Artikel, für „So viel wiegen 3 …" */
  viele: string
}

export const VERGLEICHE: readonly Vergleich[] = [
  { kg: 4, eins: "eine Hauskatze", viele: "Hauskatzen" },
  { kg: 15, eins: "ein voller Kasten Wasser", viele: "Kästen Wasser" },
  { kg: 30, eins: "ein Schäferhund", viele: "Schäferhunde" },
  { kg: 78, eins: "ein erwachsener Mensch", viele: "erwachsene Menschen" },
  { kg: 180, eins: "ein Kühlschrank samt Waschmaschine", viele: "Kühlschränke samt Waschmaschine" },
  { kg: 300, eins: "ein Klavier", viele: "Klaviere" },
  { kg: 600, eins: "ein Pferd", viele: "Pferde" },
  { kg: 1_200, eins: "ein Kleinwagen", viele: "Kleinwagen" },
  { kg: 2_500, eins: "ein Nashorn", viele: "Nashörner" },
  { kg: 6_000, eins: "ein afrikanischer Elefant", viele: "afrikanische Elefanten" },
  { kg: 13_000, eins: "ein Reisebus", viele: "Reisebusse" },
  { kg: 40_000, eins: "ein voll beladener Sattelzug", viele: "beladene Sattelzüge" },
  { kg: 77_000, eins: "ein startbereiter Airbus A320", viele: "startbereite Airbus A320" },
  { kg: 190_000, eins: "ein Blauwal", viele: "Blauwale" },
  { kg: 400_000, eins: "eine vollgetankte Boeing 747", viele: "vollgetankte Boeing 747" },
  { kg: 2_000_000, eins: "ein Space Shuttle beim Start", viele: "Space Shuttles beim Start" },
  { kg: 10_100_000, eins: "der Eiffelturm", viele: "Eiffeltürme" },
  { kg: 52_000_000, eins: "die Titanic", viele: "Titanics" },
  { kg: 5_900_000_000, eins: "die Cheops-Pyramide", viele: "Cheops-Pyramiden" },
]

/** Ab hier lohnt die Mehrzahl — darunter liest sich „1 Pferd" albern. */
const MEHRZAHL_AB = 1.6

/** Satz zum bewegten Gewicht — null, wenn noch zu wenig für einen Vergleich. */
export function vergleichSatz(kg: number): string | null {
  if (!(kg > 0)) return null
  let treffer: Vergleich | null = null
  for (const v of VERGLEICHE) if (kg >= v.kg) treffer = v
  if (!treffer) return null

  const faktor = kg / treffer.kg
  if (faktor < MEHRZAHL_AB) return `So viel wiegt ${treffer.eins}.`
  return `So viel wiegen ${Math.round(faktor)} ${treffer.viele}.`
}

/** Nächste Stufe als Ausblick: „Noch 1.520 kg bis zum Pferd." */
export function naechsterVergleich(kg: number): { fehlen: number; ziel: Vergleich } | null {
  const ziel = VERGLEICHE.find((v) => v.kg > kg)
  return ziel ? { fehlen: Math.round(ziel.kg - kg), ziel } : null
}
